import React, { useEffect, useState } from "react";
import { TriangleAlert, CircleAlert } from "lucide-react";

interface TabChangePenaltyOverlayProps {
  show: boolean;
  onComplete: () => void;
  stackPosition?: number;
  violationNumber?: number;
}

export const TabChangePenaltyOverlay: React.FC<TabChangePenaltyOverlayProps> = ({
  show,
  onComplete,
  stackPosition = 0,
  violationNumber = 1,
}) => {
  const [timeLeft, setTimeLeft] = useState(5);

  useEffect(() => {
    if (!show) {
      setTimeLeft(5);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onComplete();
          return 5;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="w-[480px] h-[478px] p-10 bg-[#131314] rounded-3xl border border-[#ec3f3f] flex flex-col justify-start items-start gap-2.5 overflow-hidden">
        <div className="self-stretch h-[398px] flex flex-col justify-center items-center gap-10">
          {/* Header Section with Icon and Text */}
          <div className="self-stretch h-[180px] flex flex-col justify-start items-center gap-3">
            {/* Icon Container */}
            <div className="w-[60px] h-[60px] relative">
              <div className="w-[60px] h-[60px] absolute bg-[#ec4040]/20 rounded-full"></div>
              <div className="absolute left-[16px] top-[16px] w-7 h-7 flex items-center justify-center">
                <TriangleAlert className="w-7 h-7 text-[#ec4040]" />
              </div>
            </div>

            {/* Title and Subtitle */}
            <div className="self-stretch h-[52px] flex flex-col justify-start items-center gap-1">
              <div className="text-center text-white text-2xl font-bold font-satoshi leading-7">
                Tab Switch Detected
              </div>
              <div className="self-stretch text-center text-white text-base font-normal font-satoshi leading-tight">
                Monitoring Violation
              </div>
            </div>

            {/* Description */}
            <div className="self-stretch text-center text-white/70 text-base font-normal font-satoshi">
              You switched away from this tab during the proctored session.
              This is a monitoring violation
            </div>
          </div>

          {/* Violation Counter Circle with Timer */}
          <div className="w-[100px] h-[100px] relative">
            {/* Background circle */}
            <svg className="w-[100px] h-[100px] transform -rotate-90" viewBox="0 0 100 100">
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                fill="none" 
                stroke="#6f727e" 
                strokeWidth="6"
              />
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                fill="none" 
                stroke="#e31212" 
                strokeWidth="6"
                strokeDasharray="282.74" 
                strokeDashoffset={282.74 - (282.74 * (5 - timeLeft)) / 5}
                className="transition-all duration-1000 ease-linear"
                strokeLinecap="round"
              />
            </svg>
            {/* Timer countdown */}
            <div className="absolute inset-0 flex items-center justify-center text-white text-4xl font-bold font-satoshi">
              {timeLeft}
            </div>
          </div>

          {/* Warning Message */}
          <div className="px-4 py-2 bg-[#ec4040]/20 rounded flex justify-start items-center gap-2">
            <CircleAlert className="w-5 h-5 text-[#ec4040]" />
            <div className="text-center text-[#ec4040] text-base font-normal font-satoshi">
              Please keep this tab active during the session
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};  